import React from 'react';
import {List} from 'immutable';
import cx from 'classnames';
import {capitalize} from 'lodash';
import {GradeLevelV1} from '@albert-io/models';
import {Text, Dropdown, MenuListItem, Button, Icon} from '@albert-io/atomic';

import useMandarkQueries from 'lib/hooks/useMandarkQueries';
import {callAction} from 'client/framework';
import {getGradeLevelsQuery} from 'client/Classrooms/Teacher/NewClassroomModal/NewClassroomModal.react';

import {QuestionEditorIcon} from '../QuestionEditorIcon/QuestionEditorIcon';
import questionEditorActions from '../QuestionEditor/QuestionEditor.actions';

import './question-grade-level-editor.scss';

export const getSortedGradeKey = (grade: string): number => {
  const num = Number(grade);
  return Number.isNaN(num) ? Infinity : num;
};

const getSortedGradeLevels = (gradeLevels: List<GradeLevelV1>) => {
  return gradeLevels.sort(
    (a, b) => getSortedGradeKey(a.getGradeLevelType()) - getSortedGradeKey(b.getGradeLevelType())
  );
};

interface QuestionGradeLevelEditorProps {
  gradeLevels?: List<GradeLevelV1>;
}

export const QuestionGradeLevelEditor = ({gradeLevels}: QuestionGradeLevelEditorProps) => {
  const {
    resources: [allGradeLevels]
  } = useMandarkQueries<[List<GradeLevelV1>]>([getGradeLevelsQuery()]);

  const gradeLevelOptions = getSortedGradeLevels(allGradeLevels)
    .map((gradeLevel) => ({
      value: gradeLevel.getId(),
      label: capitalize(gradeLevel.getGradeLevelType())
    }))
    .toArray();

  const handleGradeLevelClick = (gradeLevelId: string) => {
    const gradeLevelResource = allGradeLevels.find(
      (gradeLevel) => gradeLevel.getId() === gradeLevelId
    );
    if (gradeLevelResource) {
      callAction(questionEditorActions.UPDATE_QUESTION_GRADE_LEVELS, gradeLevelResource);
    }
  };

  const hasSelections = !!gradeLevels?.size;
  const buttonText = hasSelections
    ? getSortedGradeLevels(gradeLevels)
        .map((gradeLevel) => capitalize(gradeLevel.getGradeLevelType()))
        .join(', ')
    : 'Select one or more';

  return (
    <div className='u-display_flex u-flex-direction_column u-gap_space-x1'>
      <div className='u-display_flex u-justify-content_space-between u-align-items_center'>
        <div className='u-display_flex u-gap_space-x4 u-align-items_center'>
          <QuestionEditorIcon icon={['fal', 'chalkboard']} />
          <Text bold>Grade Level</Text>
        </div>
        <Dropdown
          trigger={
            <Button variant='outlined' color='secondary'>
              <Text
                color={hasSelections ? 'primary' : 'tertiary'}
                className='question-grade-level-editor__button-text'
              >
                {buttonText}
              </Text>
              <Icon className='u-mar-l_1' icon='caret-down' />
            </Button>
          }
          positionFixed
          position='bottom-end'
          trayClassName='question-grade-level-editor__tray'
          closeOnItemClicked={false}
        >
          <div className='u-pad_1'>
            {gradeLevelOptions.map((option) => {
              const {value, label} = option;
              const isSelected = gradeLevels?.some((gradeLevel) => gradeLevel.getId() === value);
              return (
                <MenuListItem
                  key={value}
                  className={cx('question-grade-level-editor__option', {
                    'question-grade-level-editor__option--selected': isSelected
                  })}
                  onClick={() => handleGradeLevelClick(value)}
                >
                  <Text color={isSelected ? 'info' : 'primary'}>{label}</Text>
                  {isSelected && <Icon icon='check' color='info' />}
                </MenuListItem>
              );
            })}
          </div>
        </Dropdown>
      </div>
    </div>
  );
};
